// The 13F activity on a stretch of the AAPL chain of 9 Aug 2024 (expiry
// 20 Sep 2024), as Tickerback shows it: each fund sits on the strike nearest
// its estimated trade price. Prices, shares and dates come from the app's own
// data (h13f_strike_overlay, cross-checked with SEC EDGAR). The estimated
// price is the midpoint of the quarter's range: a 13F says how many shares a
// fund held, not the price it traded at.
export const INST_CHAIN = [
  { k: 175, cb: 42.0, ca: 42.95, pb: 0.47, pa: 0.5, funds: ['TG'] },
  { k: 180, cb: 37.6, ca: 38.3, pb: 0.64, pa: 0.67, funds: ['BE', 'VI', 'CO', 'BR', 'AQ', 'SO'] },
  { k: 185, cb: 32.85, ca: 33.3, pb: 0.87, pa: 0.91, funds: ['AP', 'SO'] },
  { k: 190, cb: 28.35, ca: 28.55, pb: 1.21, pa: 1.25, funds: ['RE'] },
];

// action: new | increase | reduce | exit; q: quarter end; filed: filing date
export const FUNDS = {
  TG: { name: 'Tiger Global', legal: 'Tiger Global Management LLC', action: 'exit', shares: -851000, price: 177.04, q: '2023-06-30', filed: '2023-08-14', hue: 18 },
  BE: { name: 'Berkshire', legal: 'Berkshire Hathaway Inc', action: 'reduce', shares: -116191550, price: 182.09, q: '2024-03-31', filed: '2024-05-15', hold: 789368450, avg: 39.47, hue: 330 },
  VI: { name: 'Viking', legal: 'Viking Global Investors LP', action: 'new', shares: 3870990, price: 182.09, q: '2024-03-31', filed: '2024-05-15', hue: 120 },
  CO: { name: 'Coatue', legal: 'Coatue Management LLC', action: 'exit', shares: -2444368, price: 182.09, q: '2024-03-31', filed: '2024-05-15', hue: 265 },
  BR: { name: 'Bridgewater', legal: 'Bridgewater Associates LP', action: 'increase', shares: 1841045, price: 182.09, q: '2024-03-31', filed: '2024-05-14', hue: 300 },
  AQ: { name: 'AQR', legal: 'AQR Capital Management LLC', action: 'increase', shares: 336669, price: 182.09, q: '2024-03-31', filed: '2024-05-15', hold: 8409802, avg: 36.1, hue: 200 },
  SO: { name: 'Soros', legal: 'Soros Fund Management LLC', action: 'exit', shares: -41233, price: 182.5, q: '2023-12-31', filed: '2024-02-14', hue: 170 },
  AP: { name: 'Appaloosa', legal: 'Appaloosa LP', action: 'exit', shares: -480000, price: 183.44, q: '2023-09-30', filed: '2023-11-14', hue: 90 },
  RE: { name: 'Renaissance', legal: 'Renaissance Technologies LLC', action: 'increase', shares: 1307877, price: 190.84, q: '2024-06-30', filed: '2024-08-09', hold: 1721444, avg: 187.5, hue: 45 },
};

export const INST_COPY = {
  es: {
    caption: 'AAPL · cadena del 9 ago 2024 · vence el 20 sep 2024',
    head: ['BID', 'ASK', 'STRIKE', 'BID', 'ASK'],
    groups: ['CALLS', 'PUTS'],
    actions: { new: 'Compró · Posición nueva', increase: 'Compró · Aumentó', reduce: 'Vendió · Redujo', exit: 'Vendió · Salió del todo' },
    quarter: (q) => {
      const [y, m] = q.split('-');
      return `${{ '03': '1.er', '06': '2.º', '09': '3.er', '12': '4.º' }[m]} trimestre de ${y}`;
    },
    shares: 'Acciones', price: 'Precio medio estimado', capital: 'Capital estimado', filed: 'Informe público',
    holds: (n, avg) => `Después conservaba ${n} acciones, con un coste medio estimado de ${avg}.`,
    sameDay: 'Lo publicó el mismo día de esta cadena: en la app aparece en cuanto la SEC lo hace público.',
    empty: 'Pulsa una ficha para ver qué fondo es, qué hizo con sus acciones y a qué precio.',
    meaningTitle: 'Qué significa',
    meaning: 'La ficha va en el strike más cercano al precio al que el fondo compró o vendió. Ese precio es una estimación: el punto medio del rango del trimestre, porque el 13F declara acciones, no precios.',
    clueTitle: 'Qué te dice',
    clue: (f) =>
      ({
        new: `${f} entró por primera vez en esta zona de precio. Para ti, una pista de dónde un gran inversor vio una oportunidad de entrada.`,
        increase: `${f} compró más en esta zona. Una pista de un nivel donde un gran inversor siguió comprando.`,
        reduce: `${f} vendió parte de su posición aquí. Una pista de un nivel donde hubo presión vendedora.`,
        exit: `${f} salió del todo en esta zona. Una pista de dónde un gran inversor dio la operación por terminada.`,
      }),
    disclaimer: 'Es contexto educativo, no una recomendación: los 13F llegan hasta 45 días después del trimestre.',
    aria: (name, k) => `${name}, strike ${k}`,
  },
  en: {
    caption: 'AAPL · chain of Aug 9, 2024 · expires Sep 20, 2024',
    head: ['BID', 'ASK', 'STRIKE', 'BID', 'ASK'],
    groups: ['CALLS', 'PUTS'],
    actions: { new: 'Bought · New position', increase: 'Bought · Added', reduce: 'Sold · Trimmed', exit: 'Sold · Exited' },
    quarter: (q) => {
      const [y, m] = q.split('-');
      return `Q${{ '03': 1, '06': 2, '09': 3, '12': 4 }[m]} ${y}`;
    },
    shares: 'Shares', price: 'Estimated average price', capital: 'Estimated capital', filed: 'Public filing',
    holds: (n, avg) => `Afterwards it still held ${n} shares, at an estimated average cost of ${avg}.`,
    sameDay: 'It was filed on the very day of this chain: the app shows it as soon as the SEC makes it public.',
    empty: 'Tap a badge to see which fund it is, what it did with its shares and at what price.',
    meaningTitle: 'What it means',
    meaning: 'The badge sits on the strike nearest the price at which the fund bought or sold. That price is an estimate: the midpoint of the quarter’s range, because a 13F declares shares, not prices.',
    clueTitle: 'What it tells you',
    clue: (f) =>
      ({
        new: `${f} came in for the first time around this price. For you, a clue about where a large investor saw an entry.`,
        increase: `${f} bought more around here. A clue about a level where a large investor kept buying.`,
        reduce: `${f} sold part of its position here. A clue about a level with selling pressure.`,
        exit: `${f} sold everything around this price. A clue about where a large investor closed the trade.`,
      }),
    disclaimer: 'It is educational context, not a recommendation: 13F filings arrive up to 45 days after the quarter.',
    aria: (name, k) => `${name}, strike ${k}`,
  },
};
